import { useState } from 'react'
import { useParams, Link, Navigate } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import {
  ArrowLeftIcon,
  BeakerIcon,
  ListBulletIcon,
  TrashIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'
import {
  GET_RAW_MATERIAL,
  GET_RAW_MATERIALS,
  GET_RECIPE_INGREDIENTS,
} from '../../graphql/costing-queries'
import {
  UPDATE_RAW_MATERIAL,
  SET_RECIPE_INGREDIENTS,
} from '../../graphql/costing-mutations'
import { RawMaterial, RecipeIngredient } from '../../types/costing'
import { formatCostPerUnit, formatCost } from '../../lib/costingUtils'
import LoadingSpinner from '../../components/LoadingSpinner'
import toast from 'react-hot-toast'

type Tab = 'info' | 'composition'

const RecipeDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const recipeId = parseInt(id!)
  const [activeTab, setActiveTab] = useState<Tab>('info')

  const { data, loading, error } = useQuery(GET_RAW_MATERIAL, {
    variables: { id: recipeId },
    skip: !id,
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !data?.rawMaterial || data.rawMaterial.type !== 'recipe') {
    return <Navigate to="/costing/recipes" replace />
  }

  const recipe: RawMaterial = data.rawMaterial

  const tabs = [
    { id: 'info' as Tab, name: 'Info', icon: BeakerIcon },
    { id: 'composition' as Tab, name: 'Composition', icon: ListBulletIcon },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/costing/recipes"
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-semibold text-gray-900">{recipe.name}</h1>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700">
                recipe
              </span>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Unit: <span className="font-medium">{recipe.unit}</span>
              {' · '}
              Output: <span className="font-medium">{recipe.output_quantity ?? '—'} {recipe.unit}</span>
              {' · '}
              Cost: <span className="font-medium">{formatCostPerUnit(recipe.cost_per_unit_millicents, recipe.unit)}</span>
            </p>
          </div>
        </div>
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
          recipe.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
        }`}>
          {recipe.active ? 'Active' : 'Inactive'}
        </span>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-5 w-5 mr-2" />
                {tab.name}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'info' && <InfoTab recipe={recipe} recipeId={recipeId} />}
        {activeTab === 'composition' && <CompositionTab recipeId={recipeId} recipe={recipe} />}
      </div>
    </div>
  )
}

/* ─── Info Tab ────────────────────────────────────────────────────────────── */

const InfoTab = ({ recipe, recipeId }: { recipe: RawMaterial; recipeId: number }) => {
  const [name, setName] = useState(recipe.name)
  const [outputQuantity, setOutputQuantity] = useState(String(recipe.output_quantity ?? ''))
  const [notes, setNotes] = useState(recipe.notes ?? '')
  const [active, setActive] = useState(recipe.active)

  const [updateRawMaterial, { loading }] = useMutation(UPDATE_RAW_MATERIAL, {
    refetchQueries: [
      { query: GET_RAW_MATERIAL, variables: { id: recipeId } },
      { query: GET_RAW_MATERIALS, variables: { filters: { type: 'recipe' } } },
    ],
    onCompleted: () => toast.success('Saved'),
    onError: (err) => toast.error(`Failed to save: ${err.message}`),
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) { toast.error('Name is required'); return }
    const qty = parseFloat(outputQuantity)
    if (isNaN(qty) || qty <= 0) { toast.error('Output quantity must be greater than 0'); return }
    updateRawMaterial({
      variables: {
        id: recipeId,
        data: {
          name: name.trim(),
          notes: notes.trim() || null,
          active,
          output_quantity: qty,
        },
      },
    })
  }

  return (
    <form onSubmit={handleSubmit} className="card p-6 space-y-5 max-w-2xl">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="input w-full"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
        <span className="inline-flex items-center px-3 py-1.5 rounded bg-gray-100 text-sm text-gray-700">
          {recipe.unit}
        </span>
        <p className="mt-1 text-xs text-gray-500">Unit cannot be changed after creation.</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          This recipe produces
        </label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0.001"
            step="any"
            value={outputQuantity}
            onChange={(e) => setOutputQuantity(e.target.value)}
            className="input w-40"
          />
          <span className="text-sm text-gray-600 font-medium">{recipe.unit}</span>
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Measured output of one batch. Cost per {recipe.unit} = total ingredient cost ÷ this quantity.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="input w-full"
          placeholder="Optional notes about this recipe"
        />
      </div>

      <div className="flex items-center gap-3">
        <input
          id="active-toggle"
          type="checkbox"
          checked={active}
          onChange={(e) => setActive(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600"
        />
        <label htmlFor="active-toggle" className="text-sm font-medium text-gray-700">
          Active
        </label>
      </div>

      <div className="pt-2">
        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? 'Saving…' : 'Save Changes'}
        </button>
      </div>
    </form>
  )
}

/* ─── Unit helpers ────────────────────────────────────────────────────────── */

const UNIT_FAMILIES: Record<string, string[]> = {
  g:     ['g', 'kg'],
  kg:    ['g', 'kg'],
  ml:    ['ml', 'l'],
  l:     ['ml', 'l'],
  piece: ['piece'],
}
const TO_BASE: Record<string, number> = { g: 1, kg: 1000, ml: 1, l: 1000, piece: 1 }

function getCompatibleUnits(baseUnit: string): string[] {
  return UNIT_FAMILIES[baseUnit] ?? [baseUnit]
}

function convertToBaseUnit(qty: number, inputUnit: string, baseUnit: string): number {
  return (qty * (TO_BASE[inputUnit] ?? 1)) / (TO_BASE[baseUnit] ?? 1)
}

function smartUnitAndQty(qty: number, baseUnit: string): { unit: string; qty: string } {
  if (baseUnit === 'g'  && Math.abs(qty) >= 1000) return { unit: 'kg', qty: String(qty / 1000) }
  if (baseUnit === 'ml' && Math.abs(qty) >= 1000) return { unit: 'l',  qty: String(qty / 1000) }
  return { unit: baseUnit, qty: String(qty) }
}

/* ─── Composition Tab ─────────────────────────────────────────────────────── */

interface DraftIngredient {
  child_id: number
  child_name: string
  child_unit: string   // base unit stored by API
  inputUnit: string    // unit the user is typing in
  quantity: string     // string for controlled input
}

const CompositionTab = ({ recipeId, recipe }: { recipeId: number; recipe: RawMaterial }) => {
  const [draft, setDraft] = useState<DraftIngredient[]>([])
  const [loaded, setLoaded] = useState(false)

  // Candidate for new ingredient
  const [newChildId, setNewChildId] = useState<number | ''>('')
  const [newQuantity, setNewQuantity] = useState('')
  const [newInputUnit, setNewInputUnit] = useState('')

  const { loading: ingredientsLoading } = useQuery(GET_RECIPE_INGREDIENTS, {
    variables: { parentId: recipeId },
    skip: loaded,
    onCompleted: (data) => {
      const ingredients: RecipeIngredient[] = data.recipeIngredients ?? []
      setDraft(
        ingredients.map((i) => {
          const { unit, qty } = smartUnitAndQty(i.quantity, i.child.unit)
          return {
            child_id: i.child_id,
            child_name: i.child.name,
            child_unit: i.child.unit,
            inputUnit: unit,
            quantity: qty,
          }
        })
      )
      setLoaded(true)
    },
  })

  const { data: materialsData } = useQuery(GET_RAW_MATERIALS, {
    variables: { filters: {} },
  })

  const [setRecipeIngredients, { loading: saving }] = useMutation(SET_RECIPE_INGREDIENTS, {
    refetchQueries: [
      { query: GET_RECIPE_INGREDIENTS, variables: { parentId: recipeId } },
      { query: GET_RAW_MATERIAL, variables: { id: recipeId } },
      { query: GET_RAW_MATERIALS, variables: { filters: { type: 'recipe' } } },
    ],
    onCompleted: () => toast.success('Composition saved'),
    onError: (err) => toast.error(err.message),
  })

  const allMaterials: RawMaterial[] = materialsData?.rawMaterials ?? []
  const availableMaterials = allMaterials.filter((m) => m.id !== recipeId)

  const newSelectedMaterial = availableMaterials.find((m) => m.id === newChildId)
  const newCompatibleUnits = newSelectedMaterial ? getCompatibleUnits(newSelectedMaterial.unit) : []

  const handleNewMaterialChange = (id: number | '') => {
    setNewChildId(id)
    const mat = availableMaterials.find((m) => m.id === id)
    setNewInputUnit(mat?.unit ?? '')
    setNewQuantity('')
  }

  const handleAddRow = () => {
    if (!newChildId) { toast.error('Select an ingredient'); return }
    const qty = parseFloat(newQuantity)
    if (isNaN(qty) || qty <= 0) { toast.error('Enter a valid quantity'); return }
    const mat = availableMaterials.find((m) => m.id === newChildId)
    if (!mat) return
    if (draft.some((d) => d.child_id === newChildId)) {
      toast.error('That ingredient is already in the list. Edit its quantity directly.')
      return
    }
    setDraft((prev) => [
      ...prev,
      { child_id: mat.id, child_name: mat.name, child_unit: mat.unit, inputUnit: newInputUnit || mat.unit, quantity: newQuantity },
    ])
    setNewChildId('')
    setNewQuantity('')
    setNewInputUnit('')
  }

  const handleRemoveRow = (childId: number) => {
    setDraft((prev) => prev.filter((d) => d.child_id !== childId))
  }

  const handleQuantityChange = (childId: number, value: string) => {
    setDraft((prev) => prev.map((d) => d.child_id === childId ? { ...d, quantity: value } : d))
  }

  const handleUnitChange = (childId: number, unit: string) => {
    setDraft((prev) => prev.map((d) => d.child_id === childId ? { ...d, inputUnit: unit } : d))
  }

  const handleSave = () => {
    for (const d of draft) {
      const qty = parseFloat(d.quantity)
      if (isNaN(qty) || qty <= 0) { toast.error('All quantities must be greater than 0'); return }
    }
    setRecipeIngredients({
      variables: {
        parentId: recipeId,
        ingredients: draft.map((d) => ({
          child_id: d.child_id,
          quantity: convertToBaseUnit(parseFloat(d.quantity), d.inputUnit, d.child_unit),
        })),
      },
    })
  }

  if (ingredientsLoading && !loaded) {
    return (
      <div className="flex items-center justify-center h-40">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Cost summary */}
      <div className="card p-4 flex items-center justify-between">
        <div>
          <p className="text-xs text-gray-500 uppercase tracking-wide">Computed cost per {recipe.unit}</p>
          <p className="text-2xl font-bold text-gray-900 mt-0.5">
            {formatCost(recipe.cost_per_unit_millicents)}
          </p>
        </div>
        <div className="text-sm text-gray-500 text-right">
          <p>Output: <span className="font-medium">{recipe.output_quantity ?? '—'} {recipe.unit}</span></p>
          <p className="mt-0.5 text-xs">Cost = ingredient total ÷ output qty</p>
        </div>
      </div>

      {/* Ingredient list */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-base font-medium text-gray-900">Ingredients</h3>
          {draft.length > 0 && (
            <span className="text-xs text-gray-500">{draft.length} ingredient{draft.length !== 1 ? 's' : ''}</span>
          )}
        </div>

        {draft.length === 0 ? (
          <p className="px-6 py-8 text-sm text-gray-500 text-center">
            No ingredients yet. Add one below.
          </p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ingredient</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                <th className="px-6 py-3" />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {draft.map((row) => {
                const compatibleUnits = getCompatibleUnits(row.child_unit)
                return (
                  <tr key={row.child_id}>
                    <td className="px-6 py-3 text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <BeakerIcon className="h-4 w-4 text-gray-400 shrink-0" />
                        {row.child_name}
                      </div>
                    </td>
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min="0.001"
                          step="any"
                          value={row.quantity}
                          onChange={(e) => handleQuantityChange(row.child_id, e.target.value)}
                          className="input w-24 text-sm"
                        />
                        {compatibleUnits.length > 1 ? (
                          <select
                            value={row.inputUnit}
                            onChange={(e) => handleUnitChange(row.child_id, e.target.value)}
                            className="input text-sm py-1 w-16"
                          >
                            {compatibleUnits.map((u) => <option key={u} value={u}>{u}</option>)}
                          </select>
                        ) : (
                          <span className="text-xs text-gray-500 w-10">{row.child_unit}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleRemoveRow(row.child_id)}
                        className="text-gray-400 hover:text-red-600 transition-colors p-1 rounded"
                        title="Remove"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}

        {/* Add ingredient row */}
        <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-48">
            <label className="block text-xs text-gray-600 mb-1">Add ingredient</label>
            <select
              value={newChildId}
              onChange={(e) => handleNewMaterialChange(e.target.value ? parseInt(e.target.value) : '')}
              className="input w-full text-sm"
            >
              <option value="">Select a raw material…</option>
              {availableMaterials.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} ({m.unit}){m.type === 'recipe' ? ' · recipe' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Quantity</label>
            <div className="flex items-center gap-1">
              <input
                type="number"
                min="0.001"
                step="any"
                value={newQuantity}
                onChange={(e) => setNewQuantity(e.target.value)}
                placeholder="0"
                className="input w-24 text-sm"
              />
              {newCompatibleUnits.length > 1 ? (
                <select
                  value={newInputUnit}
                  onChange={(e) => setNewInputUnit(e.target.value)}
                  className="input text-sm py-1 w-16"
                >
                  {newCompatibleUnits.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              ) : newSelectedMaterial ? (
                <span className="text-xs text-gray-500 w-10">{newSelectedMaterial.unit}</span>
              ) : null}
            </div>
          </div>
          <button
            type="button"
            onClick={handleAddRow}
            className="btn-secondary flex items-center gap-1 text-sm"
          >
            <PlusIcon className="h-4 w-4" />
            Add
          </button>
        </div>
      </div>

      {/* Save */}
      <div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="btn-primary"
        >
          {saving ? 'Saving…' : 'Save Composition'}
        </button>
        <p className="mt-2 text-xs text-gray-500">
          Saves all ingredients at once. Replaces the previous composition.
        </p>
      </div>
    </div>
  )
}

export default RecipeDetailPage
